// ─── Studio Toolbar ───────────────────────────────────────────
// Simplified toolbar for the freeform Studio mode.
// +Image, +Text, +Shape, +Ornament, +Widget dropdown, Background (color + image).

import React, { useState, useCallback } from 'react';
import {
  Image as ImageIcon, Type, Plus, Palette, Upload,
  X, Clock, Grid3X3, MessageSquare, MapPin, Layers, Grid,
  Minus, Square, Circle, Sparkles, Music2, Play, Globe, Rows3
} from 'lucide-react';
import { ORNAMENTS, ORNAMENT_CATEGORIES } from '../../lib/ornaments';
import PreviewOnPhoneButton from './PreviewOnPhoneButton';
import MobileHealthCheckButton from './MobileHealthCheckButton';
import { applyImageRole, IMAGE_ROLES, IMAGE_ROLE_OPTIONS } from '../../lib/utils/imageRoles';

const WIDGET_TYPES = [
  { type: 'hero', label: 'Hero', icon: ImageIcon, desc: 'Full-screen intro with photo' },
  { type: 'timeline', label: 'Timeline', icon: Clock, desc: 'Event schedule' },
  { type: 'gallery', label: 'Gallery', icon: Grid3X3, desc: 'Photo grid' },
  { type: 'slideshow', label: 'Slideshow', icon: Play, desc: 'Auto-sliding image carousel' },
  { type: 'rsvp', label: 'RSVP', icon: MessageSquare, desc: 'Response form' },
  // Canvas-level RSVP button (not a full section). Marked isElement so
  // the toolbar routes it through onAddElement → handleCollageAdd.
  { type: 'rsvp_button', label: 'RSVP Button', icon: MessageSquare, desc: 'Tap-to-open dedicated RSVP page', isElement: true },
  { type: 'event-details', label: 'Event Details', icon: MapPin, desc: 'Date, time, venue' },
];

const SHAPE_TYPES = [
  { style: 'horizontal_line', label: 'Line', icon: Minus, desc: 'Horizontal divider' },
  { style: 'rectangle', label: 'Rectangle', icon: Square, desc: 'Rectangle / square' },
  { style: 'circle', label: 'Circle', icon: Circle, desc: 'Circle / ellipse' },
];

// Color presets for solid background
const BG_COLOR_PRESETS = [
  '#ffffff', '#f8f8f8', '#f5f0eb', '#f2ece6', '#ece7de',
  '#e8ddd5', '#d4cfc8', '#c9c0b5', '#b8ada0',
  '#1a1a1a', '#111111', '#2a2220', '#1c241f', '#1a1f2e',
  '#f3e8e0', '#e8d5c9', '#fce4ec', '#f3e5f5', '#e8eaf6',
];

const TEXT_TYPES = [
  { type: 'title_block', label: 'Heading', icon: Type, desc: 'Title + subtitle' },
  { type: 'text_block', label: 'Body Text', icon: Type, desc: 'Plain paragraph' },
  { type: 'text_note', label: 'Note', icon: Type, desc: 'Handwritten style' },
];

export default function StudioToolbar({
  canvasSectionId,
  onAddElement,
  onAddPhotoRow,
  onAddWidget,
  onAddShape,
  onAddOrnament,
  onUploadOrnament,
  bgColor,
  onBgColorChange,
  globalBg,
  onGlobalBgUpload,
  onGlobalBgRemove,
  globalBgMeta,
  uploadingBg,
  gridEnabled,
  onToggleGrid,
  onOpenLanguages,
  hasStaleLanguage = false,
  languagesCount = 0,
  bgScroll,
  onBgScrollChange,
  bgFit,
  onBgFitChange,
  // Phase 1.7 — QA helpers
  rsvpPageInfo,
  eventId,
  onLayoutVersionChange,
  onSelectElement,
  onSnapToEdges,
  // Phase 1.8 — designer guides
  guidesEnabled,
  onToggleGuides,
}) {
  const [showWidgets, setShowWidgets] = useState(false);
  const [showBgPanel, setShowBgPanel] = useState(false);
  const [showShapes, setShowShapes] = useState(false);
  const [showOrnaments, setShowOrnaments] = useState(false);
  const [showTextTypes, setShowTextTypes] = useState(false);
  const [customColor, setCustomColor] = useState(bgColor || '#ffffff');

  const handleAddImage = useCallback(() => {
    onAddElement(canvasSectionId, 'image_card');
  }, [canvasSectionId, onAddElement]);

  // Phase 1.8 — Add image with a specific role. Builds a base image element,
  // applies the role's defaults, and hands it to handleCollageAdd as a full
  // element preset (which sorts out z-index + insert).
  const handleAddImageWithRole = useCallback((role) => {
    const base = {
      type: 'image_card',
      style: 'plain-photo',
      src: '',
      caption: '',
      rotation: 0,
      intent: { role: 'image', importance: 'medium', behavior: 'flexible',
                mobileBehavior: 'preserve', overlapPolicy: 'avoidText',
                animationPriority: 3 },
      // Seed legacy layout fields so applyImageRole can layer on top.
      layout: { mobile: { x: 0.5, y: 0.5, w: 0.6 } },
    };
    const withRole = applyImageRole(base, role, { preserveExistingPosition: false });
    onAddElement(canvasSectionId, withRole);
    setShowImageRoles(false);
  }, [canvasSectionId, onAddElement]);
  const [showImageRoles, setShowImageRoles] = useState(false);

  const handleAddText = useCallback((type) => {
    onAddElement(canvasSectionId, type);
    setShowTextTypes(false);
  }, [canvasSectionId, onAddElement]);

  const handleAddWidget = useCallback((widget) => {
    // Section-widgets (Gallery, Timeline, etc.) go through onAddWidget.
    // Canvas elements like RSVP Button go through onAddElement so they
    // are placed on the current canvas section, not as a new section.
    if (widget?.isElement) {
      onAddElement(canvasSectionId, widget.type);
    } else {
      onAddWidget(widget.type);
    }
    setShowWidgets(false);
  }, [onAddWidget, onAddElement, canvasSectionId]);

  const handleAddShape = useCallback((shapeStyle) => {
    onAddShape(canvasSectionId, shapeStyle);
    setShowShapes(false);
  }, [canvasSectionId, onAddShape]);

  const handleAddOrnament = useCallback((ornamentId) => {
    onAddOrnament(canvasSectionId, ornamentId);
    setShowOrnaments(false);
  }, [canvasSectionId, onAddOrnament]);

  return (
    <div className="space-y-3" data-testid="studio-toolbar">
      {/* Phase 1.7 — QA tools (Preview on phone + Check mobile layout) */}
      {rsvpPageInfo?.slug && (
        <div className="flex items-center gap-1.5 p-2 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
          <PreviewOnPhoneButton
            slug={rsvpPageInfo.slug}
            layoutVersion={rsvpPageInfo.layout_version}
            layoutMode={rsvpPageInfo.layout_mode}
            updatedAt={rsvpPageInfo.updated_at}
            isPublished={rsvpPageInfo.is_published}
            previewToken={rsvpPageInfo.preview_token}
          />
          <MobileHealthCheckButton
            slug={rsvpPageInfo.slug}
            eventId={eventId}
            layoutVersion={rsvpPageInfo.layout_version}
            layoutMode={rsvpPageInfo.layout_mode}
            isPublished={rsvpPageInfo.is_published}
            previewToken={rsvpPageInfo.preview_token}
            onLayoutVersionChange={onLayoutVersionChange}
            onSelectElement={onSelectElement}
            onSnapToEdges={onSnapToEdges}
            variant="toolbar"
          />
        </div>
      )}

      {/* Languages button — sits at the top, subtle but visible */}
      {onOpenLanguages && (
        <button
          onClick={onOpenLanguages}
          className="relative w-full flex items-center justify-between px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 hover:border-amber-300 dark:hover:border-amber-500 hover:bg-amber-50/40 dark:hover:bg-amber-500/5 transition group"
          data-testid="studio-languages-btn"
          title={hasStaleLanguage
            ? 'A translation may be outdated. Open to re-translate.'
            : undefined}
        >
          <span className="flex items-center gap-2 text-xs font-medium text-gray-700 dark:text-gray-300 group-hover:text-amber-700 dark:group-hover:text-amber-400 transition-colors">
            <Globe className="w-4 h-4" />
            Languages
          </span>
          <span className="text-[11px] text-gray-400 group-hover:text-amber-500 transition-colors">
            {languagesCount > 1 ? `${languagesCount} enabled` : 'Add language →'}
          </span>
          {hasStaleLanguage && (
            <span
              className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-amber-500 ring-2 ring-white dark:ring-gray-900"
              aria-label="Translation needs update"
              data-testid="studio-languages-stale-dot"
            />
          )}
        </button>
      )}

      {/* Quick Add Buttons — row 1: Image + Text */}
      <div className="p-3 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
        <label className="block text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2.5">
          Add to Canvas
        </label>
        <div className="grid grid-cols-2 gap-2">
          <div className="relative">
            <button
              onClick={() => {
                setShowImageRoles(v => !v);
                setShowTextTypes(false); setShowShapes(false);
                setShowOrnaments(false); setShowWidgets(false);
              }}
              className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 hover:border-blue-400 dark:hover:border-blue-500 hover:bg-blue-50/50 dark:hover:bg-blue-900/10 transition-all group"
              data-testid="studio-add-image-btn"
            >
              <ImageIcon className="w-4 h-4 text-gray-400 group-hover:text-blue-500 transition-colors" />
              <span className="text-xs font-medium text-gray-500 group-hover:text-blue-600 dark:text-gray-400 transition-colors">Image</span>
            </button>
            {showImageRoles && (
              <div className="absolute top-full left-0 right-0 mt-1 z-50 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl overflow-hidden" data-testid="image-role-popover">
                {IMAGE_ROLE_OPTIONS.filter(o => o.value !== IMAGE_ROLES.IMAGE_LAYER).map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => handleAddImageWithRole(opt.value)}
                    className="w-full flex items-start gap-3 px-3 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-left"
                    data-testid={`image-role-${opt.value}`}
                  >
                    <ImageIcon className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="text-xs font-medium text-gray-700 dark:text-gray-300">{opt.label}</div>
                      <div className="text-[10px] text-gray-400">{opt.desc}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="relative">
            <button
              onClick={() => { setShowTextTypes(v => !v); setShowShapes(false); setShowOrnaments(false); setShowWidgets(false); }}
              className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 hover:border-blue-400 dark:hover:border-blue-500 hover:bg-blue-50/50 dark:hover:bg-blue-900/10 transition-all group"
              data-testid="studio-add-text-btn"
            >
              <Type className="w-4 h-4 text-gray-400 group-hover:text-blue-500 transition-colors" />
              <span className="text-xs font-medium text-gray-500 group-hover:text-blue-600 dark:text-gray-400 transition-colors">Text</span>
            </button>
            {showTextTypes && (
              <div className="absolute top-full left-0 right-0 mt-1 z-50 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl overflow-hidden">
                {TEXT_TYPES.map(t => {
                  const TIcon = t.icon;
                  return (
                    <button
                      key={t.type}
                      onClick={() => handleAddText(t.type)}
                      className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-left"
                      data-testid={`studio-text-${t.type}`}
                    >
                      <TIcon className="w-4 h-4 text-gray-400" />
                      <div>
                        <div className="text-xs font-medium text-gray-700 dark:text-gray-300">{t.label}</div>
                        <div className="text-[10px] text-gray-400">{t.desc}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Row 2: Shape + Ornament */}
        <div className="grid grid-cols-2 gap-2 mt-2">
          <div className="relative">
            <button
              onClick={() => { setShowShapes(v => !v); setShowOrnaments(false); setShowWidgets(false); setShowTextTypes(false); }}
              className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 hover:border-emerald-400 dark:hover:border-emerald-500 hover:bg-emerald-50/50 dark:hover:bg-emerald-900/10 transition-all group"
              data-testid="studio-add-shape-btn"
            >
              <Square className="w-4 h-4 text-gray-400 group-hover:text-emerald-500 transition-colors" />
              <span className="text-xs font-medium text-gray-500 group-hover:text-emerald-600 dark:text-gray-400 transition-colors">Shape</span>
            </button>
            {showShapes && (
              <div className="absolute top-full left-0 right-0 mt-1 z-50 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl overflow-hidden">
                {SHAPE_TYPES.map(s => {
                  const SIcon = s.icon;
                  return (
                    <button
                      key={s.style}
                      onClick={() => handleAddShape(s.style)}
                      className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-left"
                      data-testid={`studio-shape-${s.style}`}
                    >
                      <SIcon className="w-4 h-4 text-gray-400" />
                      <div>
                        <div className="text-xs font-medium text-gray-700 dark:text-gray-300">{s.label}</div>
                        <div className="text-[10px] text-gray-400">{s.desc}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <div className="relative">
            <button
              onClick={() => { setShowOrnaments(v => !v); setShowShapes(false); setShowWidgets(false); setShowTextTypes(false); }}
              className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 hover:border-amber-400 dark:hover:border-amber-500 hover:bg-amber-50/50 dark:hover:bg-amber-900/10 transition-all group"
              data-testid="studio-add-ornament-btn"
            >
              <Sparkles className="w-4 h-4 text-gray-400 group-hover:text-amber-500 transition-colors" />
              <span className="text-xs font-medium text-gray-500 group-hover:text-amber-600 dark:text-gray-400 transition-colors">Ornament</span>
            </button>
            {showOrnaments && (
              <div className="absolute top-full left-0 mt-1 z-50 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl overflow-hidden w-64 max-h-72 overflow-y-auto">
                {ORNAMENT_CATEGORIES.map(cat => {
                  const items = ORNAMENTS.filter(o => o.category === cat.id);
                  if (items.length === 0) return null;
                  return (
                    <div key={cat.id}>
                      <div className="px-3 py-1.5 text-[9px] font-bold uppercase tracking-wider text-gray-400 bg-gray-50 dark:bg-gray-800/50 sticky top-0">{cat.label}</div>
                      <div className="grid grid-cols-3 gap-1 p-2">
                        {items.map(orn => (
                          <button
                            key={orn.id}
                            onClick={() => handleAddOrnament(orn.id)}
                            className="p-2 rounded-lg border border-gray-100 dark:border-gray-700 hover:border-amber-300 hover:bg-amber-50/50 dark:hover:bg-amber-900/10 transition-all flex items-center justify-center"
                            title={orn.name}
                            data-testid={`studio-ornament-${orn.id}`}
                          >
                            <svg viewBox={orn.viewBox} xmlns="http://www.w3.org/2000/svg" className="w-full h-6 text-gray-600 dark:text-gray-400" dangerouslySetInnerHTML={{ __html: orn.svg }} />
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
                {/* Upload custom ornament */}
                <div className="p-2 border-t border-gray-100 dark:border-gray-800">
                  <label
                    className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-dashed border-gray-300 dark:border-gray-600 hover:border-amber-400 cursor-pointer transition-colors"
                    data-testid="studio-upload-ornament-btn"
                  >
                    <Upload className="w-3.5 h-3.5 text-gray-400" />
                    <span className="text-[11px] font-medium text-gray-500">Upload Custom</span>
                    <input type="file" accept="image/svg+xml,image/png,image/webp" className="hidden" onChange={e => {
                      const f = e.target.files?.[0];
                      if (f && onUploadOrnament) {
                        onUploadOrnament(canvasSectionId, f);
                        setShowOrnaments(false);
                      }
                      e.target.value = '';
                    }} />
                  </label>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Widget + Grid row */}
        <div className="flex gap-2 mt-2">
          <div className="relative flex-1">
            <button
              onClick={() => { setShowWidgets(v => !v); setShowShapes(false); setShowOrnaments(false); setShowTextTypes(false); }}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-purple-400 hover:bg-purple-50/50 dark:hover:bg-purple-900/10 transition-all group"
              data-testid="studio-add-widget-btn"
            >
              <Plus className="w-3.5 h-3.5 text-gray-400 group-hover:text-purple-500 transition-colors" />
              <span className="text-xs font-medium text-gray-500 group-hover:text-purple-600 dark:text-gray-400 transition-colors">Add Widget</span>
            </button>
            {showWidgets && (
              <div className="absolute top-full left-0 right-0 mt-1 z-50 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl overflow-hidden">
                {WIDGET_TYPES.map(w => {
                  const WIcon = w.icon;
                  return (
                    <button
                      key={w.type}
                      onClick={() => handleAddWidget(w)}
                      className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-left"
                      data-testid={`studio-widget-${w.type}`}
                    >
                      <WIcon className="w-4 h-4 text-gray-400" />
                      <div>
                        <div className="text-xs font-medium text-gray-700 dark:text-gray-300">{w.label}</div>
                        <div className="text-[10px] text-gray-400">{w.desc}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <button
            onClick={onToggleGrid}
            className={`flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border transition-all ${
              gridEnabled
                ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                : 'border-gray-200 dark:border-gray-700 text-gray-400 hover:border-gray-300 hover:text-gray-500'
            }`}
            data-testid="studio-grid-toggle-btn"
            title={gridEnabled ? 'Disable snap grid' : 'Enable snap grid'}
          >
            <Grid className="w-3.5 h-3.5" />
            <span className="text-[10px] font-medium">Grid</span>
          </button>
          {/* Phase 1.8 — Show/Hide guides */}
          {onToggleGuides && (
            <button
              onClick={onToggleGuides}
              className={`flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border transition-all ${
                guidesEnabled
                  ? 'border-purple-400 bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400'
                  : 'border-gray-200 dark:border-gray-700 text-gray-400 hover:border-gray-300 hover:text-gray-500'
              }`}
              data-testid="studio-guides-toggle-btn"
              title={guidesEnabled ? 'Hide guides' : 'Show guides'}
            >
              <Grid3X3 className="w-3.5 h-3.5" />
              <span className="text-[10px] font-medium">Guides</span>
            </button>
          )}
        </div>

        {/* Music + Photo row */}
        <div className="grid grid-cols-2 gap-2 mt-2">
          <button
            onClick={() => onAddElement(canvasSectionId, 'vinyl_player')}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 hover:border-pink-400 dark:hover:border-pink-500 hover:bg-pink-50/50 dark:hover:bg-pink-900/10 transition-all group"
            data-testid="studio-add-music-btn"
          >
            <Music2 className="w-4 h-4 text-gray-400 group-hover:text-pink-500 transition-colors" />
            <span className="text-xs font-medium text-gray-500 group-hover:text-pink-600 dark:text-gray-400 transition-colors">Music</span>
          </button>
          {onAddPhotoRow && (
            <button
              onClick={() => onAddPhotoRow(canvasSectionId)}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 hover:border-emerald-400 dark:hover:border-emerald-500 hover:bg-emerald-50/50 dark:hover:bg-emerald-900/10 transition-all group"
              title="Insert 3 photos pre-arranged as a centered row"
              data-testid="studio-add-photo-row-btn"
            >
              <Rows3 className="w-4 h-4 text-gray-400 group-hover:text-emerald-500 transition-colors" />
              <span className="text-xs font-medium text-gray-500 group-hover:text-emerald-600 dark:text-gray-400 transition-colors">Photo row</span>
            </button>
          )}
        </div>
      </div>

      {/* Background Panel */}
      <div className="p-3 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
        <button
          onClick={() => setShowBgPanel(v => !v)}
          className="w-full flex items-center justify-between"
        >
          <label className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 cursor-pointer">
            Background
          </label>
          <div className="flex items-center gap-2">
            {globalBg ? (
              <div className="w-6 h-6 rounded border border-gray-200 dark:border-gray-600 overflow-hidden">
                <img src={globalBg} alt="" className="w-full h-full object-cover" />
              </div>
            ) : (
              <div className="w-6 h-6 rounded border border-gray-200 dark:border-gray-600" style={{ background: bgColor || '#ffffff' }} />
            )}
            <Palette className="w-3.5 h-3.5 text-gray-400" />
          </div>
        </button>

        {showBgPanel && (
          <div className="mt-3 space-y-3">
            {/* Solid colors */}
            <div>
              <div className="text-[10px] text-gray-400 mb-1.5">Solid Color</div>
              <div className="flex flex-wrap gap-1.5">
                {BG_COLOR_PRESETS.map(c => (
                  <button
                    key={c}
                    onClick={() => {
                      setCustomColor(c);
                      onBgColorChange(c);
                    }}
                    className={`w-6 h-6 rounded-md border-2 transition-all hover:scale-110 ${
                      bgColor === c && !globalBg ? 'border-blue-500 ring-1 ring-blue-300' : 'border-gray-200 dark:border-gray-600'
                    }`}
                    style={{ background: c }}
                    data-testid={`bg-color-${c.replace('#', '')}`}
                  />
                ))}
              </div>
              {/* Custom color input */}
              <div className="flex items-center gap-2 mt-2">
                <input
                  type="color"
                  value={customColor}
                  onChange={e => {
                    setCustomColor(e.target.value);
                    onBgColorChange(e.target.value);
                  }}
                  className="w-7 h-7 rounded cursor-pointer border-0 p-0"
                  data-testid="bg-color-custom"
                />
                <input
                  type="text"
                  value={customColor}
                  onChange={e => {
                    const v = e.target.value;
                    setCustomColor(v);
                    if (/^#[0-9a-fA-F]{6}$/.test(v)) {
                      onBgColorChange(v);
                    }
                  }}
                  onBlur={e => {
                    let v = e.target.value.trim();
                    if (!v.startsWith('#')) v = '#' + v;
                    if (/^#[0-9a-fA-F]{6}$/.test(v)) {
                      setCustomColor(v);
                      onBgColorChange(v);
                    }
                  }}
                  placeholder="#ffffff"
                  className="flex-1 px-2 py-1 text-xs font-mono bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded text-gray-700 dark:text-gray-300"
                  data-testid="bg-color-hex-input"
                />
              </div>
            </div>

            {/* Image upload */}
            <div className="border-t border-gray-100 dark:border-gray-800 pt-2.5">
              <div className="text-[10px] text-gray-400 mb-1.5">Background Image</div>
              <div className="flex items-center gap-2.5">
                <div className="w-12 h-12 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 flex-shrink-0 overflow-hidden flex items-center justify-center">
                  {globalBg ? (
                    <img src={globalBg} alt="bg" className="w-full h-full object-cover" />
                  ) : (
                    <ImageIcon className="w-4 h-4 text-gray-300" />
                  )}
                </div>
                <div className="flex flex-col gap-1">
                  <label
                    className={`px-3 py-1 rounded-lg text-[11px] font-medium cursor-pointer transition-colors text-center ${
                      uploadingBg ? 'bg-gray-100 text-gray-400' : 'bg-blue-50 text-blue-600 hover:bg-blue-100 dark:bg-blue-900/20 dark:text-blue-400'
                    }`}
                    data-testid="studio-upload-bg-btn"
                  >
                    {uploadingBg ? 'Uploading...' : globalBg ? 'Replace' : 'Upload'}
                    <input type="file" accept="image/*" className="hidden" disabled={uploadingBg} onChange={e => {
                      const f = e.target.files?.[0];
                      if (f) onGlobalBgUpload(f);
                      e.target.value = '';
                    }} />
                  </label>
                  {globalBg && (
                    <button
                      onClick={onGlobalBgRemove}
                      className="px-2 py-0.5 rounded text-[10px] text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
                      data-testid="studio-remove-bg-btn"
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
              {/* Help hint — encourages users to prep the image themselves */}
              <p className="mt-2 text-[10px] leading-relaxed text-gray-400 dark:text-gray-500" data-testid="studio-bg-hint">
                Recommended: <span className="font-medium text-gray-500 dark:text-gray-400">1080px wide</span>. For tall scroll backgrounds, match your Canvas height (up to 8000px).
              </p>

              {/* Phase Image Optimization Profiles — subtle info pill shown
                  after a successful upload. Reads optimizer response metadata
                  passed in via `globalBgMeta`. Studio-only, vanishes on
                  background removal. */}
              {globalBgMeta && globalBg && (
                <div
                  className="mt-2 inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-medium bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 border border-emerald-100 dark:border-emerald-800/50"
                  data-testid="global-bg-optimized-pill"
                >
                  <span className="w-1 h-1 rounded-full bg-emerald-500" aria-hidden="true" />
                  <span>
                    Optimized: {globalBgMeta.width} × {globalBgMeta.height}
                    {globalBgMeta.sizeBytes ? ` · ${(globalBgMeta.sizeBytes / (1024 * 1024)).toFixed(1)} MB` : ''}
                    {' · '}
                    {globalBgMeta.profile === 'full_page_background' ? 'full-page profile' : 'default profile'}
                  </span>
                </div>
              )}

              {/* Scroll behavior toggle — only when an image is set */}
              {globalBg && onBgScrollChange && (
                <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800">
                  <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5 block">
                    Scroll behavior
                  </label>
                  <div className="grid grid-cols-2 gap-1.5">
                    <button
                      onClick={() => onBgScrollChange('fixed')}
                      className={`px-2 py-2 rounded-lg text-[11px] font-medium transition-colors ${
                        (bgScroll || 'fixed') === 'fixed'
                          ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-800'
                          : 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-100 border border-transparent'
                      }`}
                      data-testid="studio-bg-scroll-fixed"
                    >
                      Stays in place
                      <span className="block text-[9px] font-normal opacity-70 mt-0.5">Background pinned</span>
                    </button>
                    <button
                      onClick={() => onBgScrollChange('scroll')}
                      className={`px-2 py-2 rounded-lg text-[11px] font-medium transition-colors ${
                        bgScroll === 'scroll'
                          ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-800'
                          : 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-100 border border-transparent'
                      }`}
                      data-testid="studio-bg-scroll-scrolls"
                    >
                      Moves with page
                      <span className="block text-[9px] font-normal opacity-70 mt-0.5">Scrolls naturally</span>
                    </button>
                  </div>

                  {/* Sub-toggle: how to size the image — available in both Fixed and Scroll modes */}
                  {onBgFitChange && (
                    <div className="mt-2 pl-2 border-l-2 border-blue-200 dark:border-blue-800">
                      <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5 block">
                        Image fit
                      </label>
                      <div className="grid grid-cols-2 gap-1.5">
                        <button
                          onClick={() => onBgFitChange('cover')}
                          className={`px-2 py-2 rounded-lg text-[10px] font-medium transition-colors ${
                            (bgFit || 'cover') === 'cover'
                              ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-800'
                              : 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-100 border border-transparent'
                          }`}
                          data-testid="studio-bg-fit-cover"
                        >
                          Cover area
                          <span className="block text-[9px] font-normal opacity-70 mt-0.5">May crop</span>
                        </button>
                        <button
                          onClick={() => onBgFitChange('contain')}
                          className={`px-2 py-2 rounded-lg text-[10px] font-medium transition-colors ${
                            bgFit === 'contain'
                              ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-800'
                              : 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-100 border border-transparent'
                          }`}
                          data-testid="studio-bg-fit-contain"
                        >
                          Show full image
                          <span className="block text-[9px] font-normal opacity-70 mt-0.5">No crop</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
